import React,{ useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./user.css";
import { useDispatch, useSelector } from 'react-redux';
import { fetchThemeData } from '../../admin/themeSlice';
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { s3ThemeAssetUrl } from "../../utils/themeAssets";

const FROM_MOBILE_APP_STORAGE_KEY = 'lcp_fromMobileApp';

function isGameStagePath(pathname) {
  const p = (pathname || "").replace(/\/+$/, "") || "/";
  return /^\/game\/stage\/[^/]+$/i.test(p);
}

/** Routes where the header Back button is hidden when opened from the mobile app WebView. */
function isHideBackTargetPath(pathname) {
  const p = pathname.replace(/\/+$/, '') || '/';
  if (p === '/' || p === '/login') return true;
  return ['/game', '/leaderboard', '/result', '/thankyou', '/unhealthy-thankyou'].includes(p);
}

function appBaseUrl() {
  const base = (process.env.REACT_APP_BASE_URL || '').trim().replace(/\/+$/, '');
  return base || '/';
}

function USER({children }) {
  const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const headerRef = useRef(null);

    const {companyLogoUrl,backButtonUrl} = useSelector(state => state.ui);
    const [logoUrl,setLogoUrl]=useState("");
    const [backUrl,setBackUrl]=useState("");
    const [blockLandscape, setBlockLandscape] = useState(false);

    const { status, user, error } = useSelector(state => state.auth);
    const { status: themeStatus, data: themeData } = useSelector(state => state.theme);
 

  const applyCustomTheme = (theme) => {
    if (!theme) return;
    const t = theme.colors && typeof theme.colors === "object" ? theme.colors : theme;
    if (t.text_color != null) document.documentElement.style.setProperty("--text-color", t.text_color);
    if (t.ui_color_1 != null) document.documentElement.style.setProperty("--ui-color-1", t.ui_color_1);
    if (t.ui_color_2 != null) document.documentElement.style.setProperty("--ui-color-2", t.ui_color_2);
    if (t.option_color != null) document.documentElement.style.setProperty("--option-color", t.option_color);
    if (t.option_text_color != null) document.documentElement.style.setProperty("--option-text-color", t.option_text_color);
  };
    
  useEffect(() => {
    if (location.pathname === "/" || /^\/login(?:\/)?$/.test(location.pathname)) return;
    if (location.pathname === "/game" || location.pathname === "/result") return;
    if (themeStatus === "idle") {
      dispatch(fetchThemeData({ themeId: null }));
    }
  }, [themeStatus, dispatch, location.pathname]);

  useEffect(()=>{
    console.log(backButtonUrl);
    console.log(companyLogoUrl);
    setBackUrl(backButtonUrl);
    setLogoUrl(companyLogoUrl);
  },[backButtonUrl,companyLogoUrl])

  const fromMobileAppInUrl = searchParams.get('fromMobileApp') === 'true';
  useEffect(() => {
    if (!fromMobileAppInUrl) return;
    try {
      sessionStorage.setItem(FROM_MOBILE_APP_STORAGE_KEY, 'true');
    } catch (_) {
      /* ignore */
    }
  }, [fromMobileAppInUrl]);

  /** True when URL has fromMobileApp=true or we persisted it (e.g. after client-side navigation). */
  const isFromMobileApp = useMemo(() => {
    let fromSession = false;
    try {
      fromSession = sessionStorage.getItem(FROM_MOBILE_APP_STORAGE_KEY) === 'true';
    } catch (_) {
      fromSession = false;
    }
    return fromMobileAppInUrl || fromSession;
  }, [fromMobileAppInUrl, location.pathname]);

  const hideBackButton = useMemo(
    () => isFromMobileApp && (isHideBackTargetPath(location.pathname) || isGameStagePath(location.pathname)),
    [isFromMobileApp, location.pathname]
  );
  const isOverlayHeaderRoute = useMemo(() => {
    const p = (location.pathname || "").replace(/\/+$/, "") || "/";
    return p === "/" || p === "/login" || p === "/game" || /^\/game\/stage\/[^/]+$/i.test(p) || p === "/leaderboard";
  }, [location.pathname]);

  useEffect(() => {
    const updateLandscapeBlock = () => {
      if (typeof window === "undefined" || !window.matchMedia) {
        setBlockLandscape(false);
        return;
      }
      const isLandscape = window.matchMedia("(orientation: landscape)").matches;
      const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;
      const isSmallScreen = window.matchMedia("(max-width: 1024px)").matches;
      setBlockLandscape(Boolean(isLandscape && isTouchDevice && isSmallScreen));
    };

    updateLandscapeBlock();
    window.addEventListener("resize", updateLandscapeBlock);
    window.addEventListener("orientationchange", updateLandscapeBlock);
    return () => {
      window.removeEventListener("resize", updateLandscapeBlock);
      window.removeEventListener("orientationchange", updateLandscapeBlock);
    };
  }, []);


  useEffect(() => {
    if (location.pathname === "/" || /^\/login(?:\/)?$/.test(location.pathname)) return;
    if (location.pathname === "/game") return;
    if (themeStatus !== "succeeded" || !themeData) return;
    applyCustomTheme(themeData);
    const body = document.body;
    const applyBodyBg = () => {
      const file =
        typeof window !== "undefined" && window.innerWidth <= 768
          ? themeData.background_mob
          : themeData.background_desk;
      const url = s3ThemeAssetUrl(file);
      if (url) {
        body.style.backgroundImage = `url(${url})`;
        body.style.backgroundSize = "cover";
        body.style.backgroundPosition = "center center";
        body.style.backgroundRepeat = "no-repeat";
        body.style.backgroundAttachment = "fixed";
        body.style.minHeight = "100vh";
      }
    };
    applyBodyBg();
    window.addEventListener("resize", applyBodyBg);
    return () => window.removeEventListener("resize", applyBodyBg);
  }, [themeStatus, themeData, location.pathname]);

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (isFromMobileApp) {
      window.GameStatus?.postMessage?.('navigate_back');
      return;
    }
    if (process.env.REACT_APP_BASE_URL) {
      window.location.assign(process.env.REACT_APP_BASE_URL);
      return;
    }
    navigate("/login");
  };

  const handleBackClick = (e) => {
    e.preventDefault();
    const path = (location.pathname || "/").replace(/\/+$/, "") || "/";
    if (path.startsWith("/game/stage/")) {
      navigate("/game");
      return;
    }
    if (path === "/game") {
      navigate("/login");
      return;
    }
    if (path === "/result" || path === "/thankyou" || path === "/unhealthy-thankyou") {
      window.location.assign(appBaseUrl());
      return;
    }
    if (path === "/rules") {
      navigate("/login");
      return;
    }
    if (backUrl) {
      window.location.assign(backUrl);
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/login");
  };

  useLayoutEffect(() => {
    const applyHeaderHeight = () => {
      const h = headerRef.current?.offsetHeight || 0;
      document.documentElement.style.setProperty("--app-header-h", `${h}px`);
    };
    applyHeaderHeight();
    window.addEventListener("resize", applyHeaderHeight);
    return () => window.removeEventListener("resize", applyHeaderHeight);
  }, [logoUrl, hideBackButton]);

  return (
    <div className={`app-shell${isOverlayHeaderRoute ? " app-shell--overlay-header" : ""}`}>
      <header className="upperaction" ref={headerRef}>
        <button type="button" className="header-logo-button" onClick={handleLogoClick} aria-label="Go to home">
          <img src={logoUrl} className="logo-holder" />
        </button>
        {!hideBackButton ? (
          <div className="back-holder">
            <button type="button" className="back-default" onClick={handleBackClick}>
              Back
            </button>
          </div>
        ) : null}
      </header>
      <div className="app-shell-content">{children}</div>
      {blockLandscape ? (
        <div className="global-rotate-overlay">
          Please rotate your device to portrait mode
        </div>
      ) : null}
    </div>
  );
}

export default USER;
